import { OfferPointsComponent } from '../../../components/offer-points/offer-points.component';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { catchError, of, timeout } from 'rxjs';
import type { SubscriptionType } from '../../../types/subscription';
import { NegotiatedOffer } from '../../../types/rest-api';
import { LicenseApiService } from '../../../services/license-api.service';
import { formatRappenChf } from '../../../utils/currency-formatter';
import {
  clearMunicipalityPlanSelection,
  clearNegotiatedOfferSelection,
  saveMunicipalityPlanSelection,
  saveNegotiatedOfferSelection,
} from '../municipality-plan-selection';
import { FreePlanJobPostingDialogComponent } from '../../../components/dialogs/free-plan-job-posting-dialog/free-plan-job-posting-dialog.component';

type DurationYears = 1 | 2 | 3;

type Cell =
  | { kind: 'check' }
  | { kind: 'cross' }
  | { kind: 'text'; text: string; variant: 'body' | 'muted' | 'blue' }
  | { kind: 'premium' };

type TableBlock =
  | { type: 'employerBanner'; title: string; subtitle: string }
  | { type: 'section'; title: string }
  | { type: 'row'; label: string; hint?: string; free: Cell; unit: Cell };

/**
 * Schuleinheit onboarding: Kostenlos + Schuleinheit cards and 2-column Leistungsübersicht.
 * Persists plan via {@link saveMunicipalityPlanSelection} before emitting (same as legacy OrderProcessProducts).
 */
@Component({
  selector: 'app-school-unit-offers',
  standalone: true,
  imports: [OfferPointsComponent, MatIcon, MatProgressSpinner, DatePipe],
  templateUrl: './school-unit-offers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './school-unit-offers.component.scss',
})
export class SchoolUnitOffersComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly licenseApi = inject(LicenseApiService);
  private readonly destroyRef = inject(DestroyRef);

  /** Only the onboarding wizard accepts deals; the upgrade dialog embeds this component too. */
  readonly showNegotiatedOffer = input(false);

  /** Negotiated Schuleinheit offer for the signed-in user; replaces the plan choices. */
  readonly offer = signal<NegotiatedOffer | null>(null);
  readonly offerLoading = signal(true);
  readonly showPlans = computed(() => !this.offerLoading() && !this.offer());
  readonly formatRappenChf = formatRappenChf;

  selectOffer = output<SubscriptionType>();

  selectedDuration: DurationYears = 1;
  extraAdmins = 0;
  featuresExpanded = false;

  readonly discounts: Record<DurationYears, number> = {
    1: 0,
    2: 0.05,
    3: 0.1,
  };

  readonly tableBlocks: TableBlock[] = [
    {
      type: 'employerBanner',
      title: 'Attraktiver Arbeitgeberauftritt durch Profil',
      subtitle:
        'Präsentiere deine Schule authentisch und ziehe die passenden Fachkräfte an!',
    },
    {
      type: 'row',
      label: 'Sichtbarkeit & Ranking',
      hint: 'Bessere Platzierung und erhöhte Reichweite',
      free: { kind: 'text', text: 'Standard', variant: 'body' },
      unit: { kind: 'premium' },
    },
    {
      type: 'row',
      label: 'Schule, Standorte und Profil',
      hint: 'Umfassendes Profil mit Bildern, Videos, Werten und Team-Vorstellung',
      free: { kind: 'text', text: 'Basis', variant: 'body' },
      unit: { kind: 'premium' },
    },
    { type: 'section', title: 'Stellenausschreibungen & Reichweite' },
    {
      type: 'row',
      label: 'Stellenausschreibungen',
      free: { kind: 'text', text: '1 kostenlos', variant: 'muted' },
      unit: { kind: 'text', text: 'Unbegrenzt', variant: 'blue' },
    },
    {
      type: 'row',
      label: 'Einzelinserat: Stellvertretung',
      free: { kind: 'text', text: '29 CHF', variant: 'muted' },
      unit: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Einzelinserat: Festanstellung',
      free: { kind: 'text', text: '199 CHF', variant: 'muted' },
      unit: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Automatisches Multiposting',
      hint: 'Deine Stelle erscheint automatisch auf bildungsstellen.ch, vslch.ch, Google und mehr',
      free: { kind: 'check' },
      unit: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Social Media Sharing-Tools',
      hint: 'Einfaches Teilen auf Instagram & LinkedIn (eigenes Profil benötigt)',
      free: { kind: 'check' },
      unit: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Website-Einbindung',
      hint: 'Schulprofil & Stellen direkt auf deiner Homepage einbinden (auf Anfrage)',
      free: { kind: 'cross' },
      unit: { kind: 'check' },
    },
    { type: 'section', title: 'Bewerbungsmanagement & Prozesse' },
    {
      type: 'row',
      label: 'Bewerbungsmanagement-System',
      free: { kind: 'check' },
      unit: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Bearbeitung von Inseraten',
      free: { kind: 'cross' },
      unit: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Zeit sparen mit Automatisierungen',
      free: { kind: 'cross' },
      unit: { kind: 'check' },
    },
    { type: 'section', title: 'Organisation & Verwaltung' },
    {
      type: 'row',
      label: 'Schulgemeinde-Account',
      free: { kind: 'cross' },
      unit: { kind: 'cross' },
    },
    {
      type: 'row',
      label: 'Zusätzliche Schuleinheitsaccounts',
      free: { kind: 'cross' },
      unit: { kind: 'cross' },
    },
    {
      type: 'row',
      label: 'Standorte der Schuleinheit',
      free: { kind: 'cross' },
      unit: { kind: 'text', text: 'Bis zu 5', variant: 'body' },
    },
    {
      type: 'row',
      label: 'Admin-Accounts',
      free: { kind: 'text', text: '1 inklusive', variant: 'muted' },
      unit: { kind: 'text', text: '1 inklusive', variant: 'body' },
    },
    {
      type: 'row',
      label: 'Zusätzliche Admins',
      free: { kind: 'cross' },
      unit: { kind: 'text', text: '+9.90 CHF/Monat', variant: 'muted' },
    },
    { type: 'section', title: 'Sicherheit & Support' },
    {
      type: 'row',
      label: 'Maximaler Datenschutz nach DSG & DSGVO',
      free: { kind: 'check' },
      unit: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Persönlicher Support',
      free: { kind: 'check' },
      unit: { kind: 'check' },
    },
    {
      type: 'row',
      label: 'Online-Kurs für Admins',
      free: { kind: 'check' },
      unit: { kind: 'check' },
    },
  ];

  ngOnInit(): void {
    if (!this.showNegotiatedOffer()) {
      this.offerLoading.set(false);
      return;
    }
    this.licenseApi
      .getNegotiatedOffer()
      .pipe(
        timeout(8000),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(offer => {
        this.offer.set(
          offer?.license_type === 'SCHOOL_UNIT_SUBSCRIPTION' ? offer : null
        );
        this.offerLoading.set(false);
      });
  }

  limitLabel(value: number | null): string {
    return value === null ? 'unbegrenzt' : String(value);
  }

  onAcceptOffer(): void {
    const offer = this.offer();
    if (!offer) return;
    saveNegotiatedOfferSelection(offer);
    clearMunicipalityPlanSelection();
    this.selectOffer.emit('SCHOOL_S');
  }

  selectDuration(d: DurationYears): void {
    this.selectedDuration = d;
  }

  incAdmins(): void {
    if (this.extraAdmins < 50) this.extraAdmins++;
  }

  decAdmins(): void {
    if (this.extraAdmins > 0) this.extraAdmins--;
  }

  toggleFeatures(): void {
    this.featuresExpanded = !this.featuresExpanded;
  }

  get discount(): number {
    return this.discounts[this.selectedDuration];
  }

  get discountPercent(): number {
    return Math.round(this.discount * 100);
  }

  get monthlySubtotalChf(): number {
    return 49 + this.extraAdmins * 9.9;
  }

  get baseAnnualSubtotalChf(): number {
    return 49 * 12 * this.selectedDuration;
  }

  get addonsAnnualSubtotalChf(): number {
    return this.extraAdmins * 9.9 * 12 * this.selectedDuration;
  }

  /** Same as legacy OrderProcessProducts `legacyTotalAnnualPrice` (12 months). */
  get annualTotalChf(): number {
    const discountedBase = this.baseAnnualSubtotalChf * (1 - this.discount);
    return discountedBase + this.addonsAnnualSubtotalChf;
  }

  pickFree(): void {
    this.dialog
      .open(FreePlanJobPostingDialogComponent, {
        data: { variant: 'onboardingConfirm' },
        maxWidth: '520px',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe(proceed => {
        if (!proceed) return;
        clearNegotiatedOfferSelection();
        saveMunicipalityPlanSelection({
          subscriptionType: 'SCHOOL_FREE',
          durationYears: 1,
          extraAdmins: 0,
          totalSchoolUnits: 1,
          monthlyTotalChf: 0,
          annualTotalChf: 0,
          discountPercent: 0,
        });
        this.selectOffer.emit('SCHOOL_FREE');
      });
  }

  pickPaid(): void {
    clearNegotiatedOfferSelection();
    saveMunicipalityPlanSelection({
      subscriptionType: 'SCHOOL_S',
      durationYears: this.selectedDuration,
      extraAdmins: this.extraAdmins,
      totalSchoolUnits: 1,
      monthlyTotalChf: this.monthlySubtotalChf,
      annualTotalChf: this.annualTotalChf,
      discountPercent: this.discountPercent,
    });
    this.selectOffer.emit('SCHOOL_S');
  }
}
